import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FornecedoresService } from '../fornecedores.service';
import { Router } from '@angular/router';
import { NotificationService } from '../../../core/notification.service';
import { CategoryService } from '../../categories/category.service';
import { CategoryDto } from '../../categories/category.model';
import { CidadeService } from '../../cidades/cidade.service';
import { CidadeCacheService } from '../../cidades/cidade-cache.service';
import { CidadeDto } from '../../cidades/cidade.model';
import { ESTADOS_BR, normalizeCidade } from '../../../shared/utils/brasil.utils';

function slugify(v: string) {
  return v
    .toString()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

@Component({
  standalone: false,
  selector: 'app-fornecedor-form',
  templateUrl: './fornecedor-form.component.html',
  styleUrls: ['./fornecedor-form.component.css']
})
export class FornecedorFormComponent implements OnInit {
  form!: FormGroup;
  saving = false;
  createdId?: string;
  initialMedias: any[] = [];
  isEditMode = false;
  categories: CategoryDto[] = [];
  cidades: CidadeDto[] = [];
  _selectedCidadesIds: string[] = [];
  get selectedCidadesIds(): string[] {
    return this._selectedCidadesIds;
  }
  set selectedCidadesIds(value: string[]) {
    this._selectedCidadesIds = value;
    // Whenever selectedCidadesIds changes, update the checkbox map
    this.cidadeCheckboxMap = {};
    this.cidades.forEach(c => {
      this.cidadeCheckboxMap[c.id] = value.includes(c.id);
    });
    console.debug('[FornecedorForm] Updated selectedCidadesIds to:', value);
    console.debug('[FornecedorForm] Updated cidadeCheckboxMap:', this.cidadeCheckboxMap);
  }
  cidadeCheckboxMap: { [key: string]: boolean } = {}; // Map for two-way binding with checkboxes
  cidadePrincipalId: string = ''; // Sincronizado com primeira cidade
  supplierUser: any = null;
  loadingSupplierUser = false;
  resending = false;
  estados = ESTADOS_BR;
  planLevels = [
    { value: 0, label: 'Free' },
    { value: 1, label: 'Vitrine' }
  ];

  constructor(private fb: FormBuilder,
              private svc: FornecedoresService,
              private router: Router,
              private notification: NotificationService,
              private categorySvc: CategoryService,
              private cidadeSvc: CidadeService,
              private cidadeCacheSvc: CidadeCacheService,
              private route: ActivatedRoute) {}

  ngOnInit(): void {
    const id = this.route?.snapshot?.paramMap?.get('id');
    this.isEditMode = !!id;

    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(200)]],
      slug: ['', [Validators.maxLength(200)]],
      descricao: ['', [Validators.maxLength(4000)]],
      cidade: ['', [Validators.maxLength(100)]],
      cidadePrincipalId: [''], // Sincronizado automaticamente
      estado: ['', [Validators.maxLength(2)]],
      cnpj: ['', [Validators.maxLength(18)]],
      telefone: ['', [Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      emailOwner: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      website: ['', [Validators.maxLength(250)]],
      instagram: ['', [Validators.maxLength(200)]],
      whatsApp: ['', [Validators.maxLength(50)]],
      facebook: ['', [Validators.maxLength(250)]],
      endereco: ['', [Validators.maxLength(500)]],
      horarioFuncionamento: ['', [Validators.maxLength(200)]],
      logoUrl: ['', [Validators.maxLength(500)]],
      ativo: [false],
      destaque: [false],
      seloFornecedor: [false],
      publicado: [false],
      rating: [null],
      categoriaId: [null],
      planLevel: [0, [Validators.required]]
    });

    this.form.controls['nome'].valueChanges.subscribe(v => {
      const currentSlug = this.form.controls['slug'].value;
      const gen = slugify(v || '');
      if (!currentSlug || currentSlug === '' ) {
        this.form.controls['slug'].setValue(gen, { emitEvent: false });
      }
    });

    // Clear server validation errors when a control value changes
    Object.keys(this.form.controls).forEach(controlName => {
      const ctrl = this.form.controls[controlName];
      ctrl.valueChanges.subscribe(() => {
        const errs: any = ctrl.errors;
        if (!errs) return;
        if (errs.server) {
          // remove only the server error key
          const copy = { ...errs };
          delete copy.server;
          const remainingKeys = Object.keys(copy);
          if (remainingKeys.length === 0) {
            ctrl.setErrors(null);
          } else {
            ctrl.setErrors(copy);
          }
        }
      });
    });

    // load categories for selection (API may return either an array or an object { data: [...] })
    this.categorySvc.listAll().subscribe((cats: any) => {
      let arr = (cats && cats.data) ? cats.data : (Array.isArray(cats) ? cats : []);
      // converter campos para camelCase
      this.categories = arr.map((c: any) => ({
        id: c.Id || c.id,
        nome: c.Nome || c.nome,
        slug: c.Slug || c.slug,
        descricao: c.Descricao || c.descricao,
        imageId: c.ImageId || c.imageId,
        imageUrl: c.ImageUrl || c.imageUrl
      }));
      console.debug('[FornecedorForm] loaded categories', this.categories);
    }, err => {
      console.error('[FornecedorForm] failed to load categories', err);
      this.categories = [];
    });

    this.loadCidades(() => {
      if (id) {
        this.loadFornecedor(id);
      }
    });
  }

  private loadCidades(afterLoad?: () => void): void {
    this.cidadeCacheSvc.getAllActive().subscribe({
      next: (cids: any) => {
        const arr = Array.isArray(cids) ? cids : (cids?.data ?? []);
        this.cidades = arr
          .filter((c: any) => c.ativo !== false)
          .map(normalizeCidade);
        this.syncCidadeCheckboxMap();
        afterLoad?.();
      },
      error: (err: any) => {
        console.error('[FornecedorForm] failed to load cidades from cache', err);
        this.cidadeSvc.listAll().subscribe({
          next: (cids: any) => {
            const arr = Array.isArray(cids) ? cids : (cids?.data ?? []);
            this.cidades = arr
              .filter((c: any) => c.ativo !== false)
              .map(normalizeCidade);
            this.syncCidadeCheckboxMap();
            afterLoad?.();
          },
          error: (err2: any) => {
            console.error('[FornecedorForm] fallback failed', err2);
            this.cidades = [];
            this.cidadeCheckboxMap = {};
            afterLoad?.();
          }
        });
      }
    });
  }

  private syncCidadeCheckboxMap(): void {
    this.cidadeCheckboxMap = {};
    this.cidades.forEach(c => {
      this.cidadeCheckboxMap[c.id] = this._selectedCidadesIds.includes(c.id);
    });
  }

  onFornecedorReloaded(updated: any) {
    if (!updated) return;
    this.initialMedias = updated.medias || updated.imagens || updated.images || [];
    // ensure createdId stays in sync
    this.createdId = updated.id || this.createdId;
  }

  private loadFornecedor(id: string) {
    this.svc.get(id).subscribe((res: any) => {
      if (!res) return;
      const planLevelValue = this.normalizePlanLevel(res.planLevel);
      // patch form values
      const patch = {
        nome: res.nome || res.nomeFantasia || '',
        slug: res.slug || '',
        descricao: res.descricao || '',
        cidade: res.cidade || '',
        cidadePrincipalId: '', // Will be set below
        estado: res.estado || res.state || '',
        cnpj: res.cnpj || '',
        telefone: res.telefone || res.phone || '',
        email: res.email || '',
        emailOwner: res.emailOwner || res.email || '',
        website: res.website || '',
        instagram: res.instagram || '',
        whatsApp: res.whatsApp || '',
        facebook: res.facebook || '',
        endereco: res.endereco || '',
        horarioFuncionamento: res.horarioFuncionamento || '',
        logoUrl: res.logoUrl || res.fotoUrl || '',
        ativo: !!res.ativo,
        destaque: !!res.destaque,
        seloFornecedor: !!res.seloFornecedor,
        publicado: !!res.publicado,
        rating: res.rating ?? null,
        categoriaId: (res.categoria && (res.categoria.id || res.categoriaId)) ? (res.categoria.id || res.categoriaId) : (res.categoriaId ?? null),
        planLevel: planLevelValue
      };
      this.form.patchValue(patch);
      this.createdId = res.id || id; // allow media manager to show
      // pre-populate media manager when API returns medias/imagens/gallery
      this.initialMedias = res.medias || res.imagens || res.images || res.gallery || res.imagens?.data || [];
      
      // pre-populate selected cities from multi-city array
      const cidadesFromApi: any[] = Array.isArray(res.cidades) ? res.cidades : [];
      const cidadesIds = cidadesFromApi
        .map((c: any) => c.id || c.Id || c.cidadeId || c.CidadeId)
        .filter(Boolean);
      this.selectedCidadesIds = Array.from(new Set(cidadesIds));
      
      // sync cidadePrincipalId with cidadePrincipal object or first city ID
      if (res.cidadePrincipalId) {
        this.cidadePrincipalId = res.cidadePrincipalId;
      } else if (res.cidadePrincipal && (res.cidadePrincipal.id || res.cidadePrincipal.Id)) {
        this.cidadePrincipalId = res.cidadePrincipal.id || res.cidadePrincipal.Id;
      } else if (this.selectedCidadesIds.length > 0) {
        this.cidadePrincipalId = this.selectedCidadesIds[0];
      }

      if (this.cidadePrincipalId && !this.selectedCidadesIds.includes(this.cidadePrincipalId)) {
        this.selectedCidadesIds = [...this.selectedCidadesIds, this.cidadePrincipalId];
      }
      
      this.form.patchValue({ cidadePrincipalId: this.cidadePrincipalId }, { emitEvent: false });

      this.syncCidadeCheckboxMap();
      
      // load supplier user info
      this.loadSupplierUser(id);
    }, err => {
      console.error('[FornecedorForm] failed to load fornecedor', err);
      this.notification.showError('Erro ao carregar fornecedor para edição');
    });
  }

  private loadSupplierUser(fornecedorId: string) {
    this.loadingSupplierUser = true;
    console.log('[FornecedorForm] Loading supplier user for fornecedor:', fornecedorId);
    this.svc.getSupplierUser(fornecedorId).subscribe({
      next: (user: any) => {
        this.supplierUser = user;
        this.loadingSupplierUser = false;
        console.log('[FornecedorForm] Supplier user loaded:', user);
      },
      error: (err: any) => {
        console.warn('[FornecedorForm] Failed to load supplier user:', err?.status, err?.statusText);
        console.warn('[FornecedorForm] Error response:', err?.error);
        
        // Check if it's a 404 or "user not found" response - treat as valid state
        if (err?.status === 404 || err?.error?.message?.includes('não encontrado')) {
          console.log('[FornecedorForm] User not found yet (expected for new fornecedores)');
          this.supplierUser = null;
        }
        
        this.loadingSupplierUser = false;
      }
    });
  }

  resendWelcomeEmail() {
    if (!this.createdId) {
      console.warn('[FornecedorForm] No createdId to resend email');
      return;
    }
    this.resending = true;
    console.log('[FornecedorForm] Resending welcome email for fornecedor:', this.createdId);
    this.svc.resendWelcomeEmail(this.createdId).subscribe({
      next: (res: any) => {
        this.resending = false;
        console.log('[FornecedorForm] Resend email success:', res);
        this.notification.showSuccess('Email reenviado com sucesso');
      },
      error: (err: any) => {
        this.resending = false;
        console.error('[FornecedorForm] Resend email failed:', err);
        console.error('[FornecedorForm] Status:', err?.status);
        console.error('[FornecedorForm] Message:', err?.message);
        this.notification.showError('Erro ao reenviar email: ' + (err?.error?.message || err?.statusText || err?.message || 'Erro desconhecido'));
      }
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      this.notification.showError('Verifique os campos obrigatórios');
      return;
    }
    
    // Validate at least one city is selected (optional but recommended)
    if (this._selectedCidadesIds.length === 0) {
      this.notification.showWarning('Recomendação: Selecione pelo menos uma cidade para as cidades atendidas');
    }
    
    this.saving = true;
    const id = this.route?.snapshot?.paramMap?.get('id');
    // include multi-city selection in payload
    const payload = { ...this.form.value, cidadesIds: this._selectedCidadesIds };
    const operation = id ? this.svc.update(id, payload) : this.svc.create(payload);
    operation.subscribe({
      next: (res: any) => {
        this.saving = false;
        // res may be null or a primitive; guard property access to avoid runtime errors
        const resId = res && (res.id || res.fornecedorId) ? (res.id || res.fornecedorId) : undefined;
        const fallbackId = typeof res === 'string' ? res : undefined;
        this.createdId = resId || fallbackId || id;
        const message = id ? 'Fornecedor atualizado com sucesso' : 'Fornecedor criado com sucesso';
        this.notification.showSuccess(message);
        // after update, navigate back to list; after create, keep on page so media manager is available
        if (id) {
          setTimeout(() => this.router.navigate(['/admin/fornecedores']), 700);
        }
      },
      error: (err: any) => {
        this.saving = false;
        if (err && err.validation && err.validation.fieldErrors) {
          const fields = err.validation.fieldErrors;
          Object.keys(fields).forEach(fieldKey => {
            const control = this.form.get(fieldKey.toLowerCase()) || this.form.get(fieldKey);
            const messages = fields[fieldKey];
            if (control) control.setErrors({ server: messages.join(' ') });
          });
        } else {
          console.error(err);
        }
        // show error toast
        this.notification.showError('Erro ao salvar fornecedor');
      }
    });
  }

  cancel() { this.router.navigate(['/admin/fornecedores']); }

  /** Returns true if the given city ID is in the selected list */
  isCidadeSelecionada(cidadeId: string): boolean {
    return this._selectedCidadesIds.includes(cidadeId);
  }

  /** Track by function for ngFor performance */
  trackByIdFn(index: number, item: any): string {
    return item.id || index.toString();
  }

  /** Toggle a city checkbox */
  toggleCidade(cidadeId: string, checked: boolean): void {
    const set = new Set(this._selectedCidadesIds);
    if (checked) {
      set.add(cidadeId);
      this.cidadeCheckboxMap[cidadeId] = true;
    } else {
      set.delete(cidadeId);
      this.cidadeCheckboxMap[cidadeId] = false;
    }
    this.selectedCidadesIds = Array.from(set);
    
    // Auto-sync cidadePrincipalId: se nenhuma cidade principal foi definida ou a seleção mudou,
    // use a primeira cidade selecionada como principal
    if (this._selectedCidadesIds.length > 0) {
      if (!this.cidadePrincipalId || !this._selectedCidadesIds.includes(this.cidadePrincipalId)) {
        this.cidadePrincipalId = this._selectedCidadesIds[0];
        this.form.patchValue({ cidadePrincipalId: this.cidadePrincipalId }, { emitEvent: false });
      }
    } else {
      // Se nenhuma cidade está selecionada, limpe a principal
      this.cidadePrincipalId = '';
      this.form.patchValue({ cidadePrincipalId: '' }, { emitEvent: false });
    }
  }

  /** Returns display label for selected cities */
  get cidadesSelecionadasLabel(): string {
    if (this._selectedCidadesIds.length === 0) return 'Nenhuma cidade selecionada';
    return this.cidades
      .filter(c => this._selectedCidadesIds.includes(c.id))
      .map(c => c.nome)
      .join(', ');
  }

  private normalizePlanLevel(plan: any): number {
    if (typeof plan === 'number') return plan;
    if (typeof plan === 'string') {
      const map: Record<string, number> = {
        low: -1,
        Low: -1,
        free: 0,
        Free: 0,
        vitrine: 1,
        Vitrine: 1
      };
      if (plan in map) return map[plan];
    }
    return 0;
  }
}
